import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useSelectedCustomer } from "@/composables/useCustomer";

export const REDIRECT_PAGE = "/login?redirect-to=/bookings-ui";


// type the meta fields
declare module "vue-router" {
  interface RouteMeta {
    auth?: boolean;
    agent?: boolean;
    admin?: boolean;
    public?: boolean;
    onSuccessRoute?: string;
    parent?: string;
  }
}

const routes = [
  {
    path: "",
    name: "Home",
    component: () => import("@/pages/home.vue"),
  },
  {
    path: "/resources",
    alias: "/resource",
    name: "Resources",
    component: () => import("@/pages/resource/resources-all.vue"),
  },
  {
    path: "/categories",
    alias: "/category",
    name: "Categories",
    component: () => import("@/pages/resource/categories.vue"),
  },
  {
    path: "/category/:categoryId",
    name: "Category",
    props: true,
    component: () => import("@/pages/resource/category.vue"),
  },
  {
    path: "/resource/:resourceId",
    name: "Resource",
    props: true,
    component: () => import("@/pages/resource/resource.vue"),
  },
  {
    path: "/booking/:bookingId",
    name: "Booking",
    props: true,
    component: () => import("@/pages/resource/booking.vue"),
  },
  {
    path: "/my-bookings",
    alias: "/booking",
    name: "My Bookings",
    component: () => import("@/pages/resource/my-bookings.vue"),
  },
  {
    path: "/book",
    name: "Book",
    props: (route) => ({ u: route.query.u, r: route.query.r, d: route.query.d }),
    component: () => import("@/pages/resource/book.vue"),
  },
  {
    path: "/cart",
    name: "Cart",
    beforeEnter() {
      const url = new URL("/checkout", location.origin);
      url.searchParams.set("webshop_customer", useSelectedCustomer().value);
      location.replace(url.toString());
    }
  },
  {
    path: "/login",
    name: "Login",
    beforeEnter() {
      const url = new URL(REDIRECT_PAGE, location.origin);
      let redirectTo = location.href.replace(location.origin, "");
      if (redirectTo === "/bookings-ui/login") {
        redirectTo = "/bookings-ui";
      }
      url.searchParams.set("redirect-to", redirectTo);
      location.replace(url.toString());
    }
  },
  {
    path: "/events",
    alias: "/event",
    name: "Events",
    component: () => import("@/pages/events/events-upcoming.vue"),
  },
  {
    path: "/events/upcoming",
    name: "EventsUpcoming",
    component: () => import("@/pages/events/events-upcoming.vue"),
  },
  // {
  //   path: "/events/all",
  //   name: "EventsAll",
  //   component: () => import("@/pages/events/events-all.vue"),
  // },
  {
    path: "/event/:id",
    name: "Event",
    props: (route) => ({ id: route.params.id }),
    component: () => import("@/pages/events/event.vue"),
  },
  {
    path: "/event-register/:eventId",
    name: "EventRegister",
    props: true,
    component: () => import("@/pages/events/event-register.vue"),
  },
  {
    path: "/my-event-registrations",
    name: "My Event Registrations",
    component: () => import("@/pages/events/my-event-registrations.vue"),
  },
  {
    path: "/credits",
    name: "CreditsHome",
    component: () => import("@/pages/credits/credits-home.vue"),
  },
  {
    path: "/credit-type/:id",
    name: "CreditType",
    props: (route) => ({ id: route.params.id }),
    component: () => import("@/pages/credits/credit-type.vue"),
  },
  {
    path: "/account",
    name: "Account",
    component: () => import("@/pages/me/account.vue"),
  },
  {
    path: "/invoices",
    name: "Invoices",
    component: () => import("@/pages/me/invoices.vue"),
  },
  {
    path: "/orders",
    name: "Orders",
    component: () => import("@/pages/me/orders.vue"),
  },
  {
    path: "/quotations",
    name: "Quotations",
    component: () => import("@/pages/me/quotations.vue"),
  },
  {
    path: "/contracts",
    name: "Contracts",
    component: () => import("@/pages/me/contracts.vue"),
  },
  {
    path: "/contract/:id",
    name: "Contract",
    props: true,
    component: () => import("@/pages/me/contract.vue"),
  },
  {
    path: "/social/public-profiles",
    name: "Published Profiles",
    component: () => import("@/pages/social/public-profiles.vue"),
  },
  {
    path: "/blog",
    name: "Blog Posts",
    component: () => import("@/pages/blog/blog-home.vue"),
  },
  {
    path: "/blog/:postId",
    name: "Blog Post",
    props: true,
    component: () => import("@/pages/blog/blog-post.vue"),
  },
  {
    path: "/kb",
    name: "Articles",
    component: () => import("@/pages/kb/articles.vue"),
  },
  {
    path: "/kb/:id",
    name: "Article",
    props: true,
    component: () => import("@/pages/kb/article.vue"),
  },
  {
    path: "/tickets",
    name: "Tickets",
    component: () => import("@/pages/support/tickets.vue"),
  },
  {
    path: "/ticket/:id",
    name: "Ticket",
    props: true,
    component: () => import("@/pages/support/ticket.vue"),
  },
  {
    path: "/documents",
    name: "Documents",
    component: () => import("@/pages/documents/documents.vue"),
  },
  {
    path: "/document/:id",
    name: "Document",
    props: true,
    component: () => import("@/pages/documents/document.vue"),
  },
  {
    path: "/members",
    name: "Members",
    component: () => import("@/pages/org/members.vue"),
  },
  {
    path: "/event-slots",
    name: "Event Slots",
    component: () => import("@/pages/event-slots/event-slots.vue"),
  },
  {
    path: "/creation-guides",
    name: "CreationGuides",
    component: () => import("@/pages/creation-guides/creation-guides.vue"),
  },
  {
    path: "/creation-guides/new",
    name: "NewCreationGuide",
    meta: { auth: true },
    component: () => import("@/pages/creation-guides/creation-guide-edit.vue"),
  },
  {
    path: "/creation-guides/:slug/edit",
    name: "EditCreationGuide",
    props: true,
    meta: { auth: true },
    component: () => import("@/pages/creation-guides/creation-guide-edit.vue"),
  },
  {
    path: "/creation-guides/:slug",
    name: "CreationGuide",
    props: true,
    component: () => import("@/pages/creation-guides/creation-guide.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "404",
    component: () => import("@/pages/404.vue"),
  },
] as Readonly<RouteRecordRaw[]>;

export const router = createRouter({
  history: createWebHistory("/bookings-ui/"),
  routes,
});
